import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {LoginDto} from "../dto/users/LoginDto";
import {UserDto} from "../dto/users/UserDto";
import { HttpClient } from "@angular/common/http";
import {RegisterDto} from "../dto/users/RegisterDto";
import {map} from "rxjs";
import {UserType} from "../enums/users/UserType";
import {Router, UrlTree} from "@angular/router";
import {environment} from "../../environments/environment.development";

import {rNames} from "../r.names";
import {controllersNames} from "../controllersNames";
import {isPlatformBrowser} from "@angular/common";

/** Ответ входа при включённом подтверждении по email: код ушёл на почту. */
export interface TwoFactorChallenge {
  twoFactor: true;
  ticket: string;
}

export const isTwoFactorChallenge = (v: UserDto | TwoFactorChallenge): v is TwoFactorChallenge =>
  (v as TwoFactorChallenge).twoFactor === true;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);


  user: UserDto | undefined = undefined;
  controller = controllersNames.auth;
  api = environment.apiBase + '/' + this.controller;
  returnUrl?: string | UrlTree;

  isLogged() {
    if (!this.user || !this.user.guid || !this.user.email || !this.user.token || (this.user.userType == UserType.Undefined)) {
      //   try to get from storage
      const fromS = this.getUserFromStorage();
      if (fromS && fromS.token && fromS.email && fromS.guid && fromS.userType) {
        this.user = fromS;
      }
    }
    return !!(this.user && this.user.guid && this.user.email && this.user.token && this.user.userType);
  }

  isLoggedAdmin() {
    return this.isLogged() && this.user?.userType == UserType.Admin;
  }

  /**
   * Статус авторизации ещё определяется: SSR — вообще не знаем,
   * в браузере — токен есть, но юзер не резолвлен (userType и т.д.).
   * Пока pending — показывать лоадер, а не «войдите».
   */
  authPending(): boolean {
    if (!isPlatformBrowser(this.platformId)) return true;
    if (this.isLogged()) return false;
    return !!this.getUserFromStorage()?.token;
  }

  isTokenExpired(): boolean {
    let user = this.getUserFromStorage();

    if (!user?.token) {
      this.logout();
      return true;
    }

    try {
      // get token expire timestamp from jwt token
      let {exp} = <{ exp: number }>JSON.parse(atob(user.token.split('.')[1]));
      if (Date.now() >= exp * 1000) {
        this.logout();
        return true;
      }
    } catch {
      // Битый или некорректный токен — считаем сессию недействительной.
      this.logout();
      return true;
    }

    return false;
  }

  getUserFromStorage() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        const raw = localStorage.getItem('user');
        return raw ? JSON.parse(raw) as UserDto : undefined;
      } catch {
        // Повреждённые данные в localStorage — очищаем их.
        this.removeUserFromStorage();
        return undefined;
      }
    }
    return undefined;

  }

  setUserToStorage(value: UserDto) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('user', JSON.stringify(value));
    }
  }

  removeUserFromStorage() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('user');
    }
  }

  updateUser(user: UserDto) {
    this.user = user;
    this.setUserToStorage(user);
  }

  /** Вход. При включённом коде из письма вернёт TwoFactorChallenge — дальше loginCode(). */
  login(dto: LoginDto) {
    return this.http.post<UserDto | TwoFactorChallenge>(this.api + '/Login', dto)
      .pipe(map(value => {
        if (!isTwoFactorChallenge(value)) {
          this.setUserToStorage(value);
          this.user = value;
        }
        return value;
      }))
  }

  /** Второй шаг входа: 6-значный код из письма. */
  loginCode(ticket: string, code: string) {
    return this.http.post<UserDto>(this.api + '/Login/Code', {ticket, code})
      .pipe(map(value => {
        this.setUserToStorage(value);
        this.user = value;
        return value;
      }));
  }

  /** «Забыли пароль?» — всегда 204, не выдаёт, есть ли такой email. */
  forgotPassword(email: string) {
    return this.http.post<void>(this.api + '/ForgotPassword', {email});
  }

  resetPassword(token: string, password: string, passwordConfirm: string) {
    return this.http.post<void>(this.api + '/ResetPassword', {token, password, passwordConfirm});
  }

  confirmEmail(token: string) {
    return this.http.post<void>(this.api + '/ConfirmEmail', {token});
  }

  register(dto: RegisterDto) {
    return this.http.post<UserDto>(this.api + '/Register', dto)
      .pipe(map(value => {
        this.setUserToStorage(value);
        this.user = value;
        return value;
      }));
  }


  logout() {
    this.user = undefined;
    this.removeUserFromStorage();
    if (this.router.url.match(/\/(account|admin).*/gi)) {
      this.router.navigate(['/', rNames.login]).finally();
    }
  }

  logoutWithRedirect(path: string = '/' + rNames.login) {
    this.logout();
    this.router.navigateByUrl(path).finally();
  }
}

